// @ts-nocheck
/**
 * TwitchChatClient - Client pour récupérer les vrais messages de chat Twitch
 * Utilise tmi.js pour se connecter à l'IRC de Twitch
 */

// Import dynamique de tmi.js depuis esm.sh
let tmi = null;

export class TwitchChatClient {
    constructor() {
        this.client = null;
        this.isConnected = false;
        this.connectedChannels = new Set();
        this.messageHandlers = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    /**
     * Initialise le client TMI
     */
    async init() {
        try {
            // Charger tmi.js dynamiquement si pas encore chargé
            if (!tmi) {
                console.warn('tmi.js n\'est pas chargé. Chargement depuis CDN...');
                await this.loadTmiJs();
            }

            // Configuration du client TMI (connexion anonyme pour lire les messages)
            this.client = new tmi.Client({
                options: {
                    debug: false,
                    messagesLogLevel: 'info'
                },
                connection: {
                    secure: true,
                    reconnect: true
                },
                channels: [] // Les canaux seront ajoutés dynamiquement
            });

            // Gestionnaires d'événements
            this.setupEventHandlers();

            // Connexion
            await this.client.connect();
            this.isConnected = true;
            this.reconnectAttempts = 0;
            
            // Client Twitch Chat connecté
            return true;
        } catch (error) {
            console.error('❌ Erreur lors de l\'initialisation du client Twitch:', error);
            return false;
        }
    }

    /**
     * Charge tmi.js depuis le CDN ESM
     */
    async loadTmiJs() {
        try {
            const tmiModule = await import('https://esm.sh/tmi.js@1.8.5');
            tmi = tmiModule.default || tmiModule;
            // tmi.js chargé avec succès depuis esm.sh
        } catch (error) {
            console.error('❌ Erreur lors du chargement de tmi.js:', error);
            throw new Error('Impossible de charger tmi.js depuis esm.sh');
        }
    }

    /**
     * Configure les gestionnaires d'événements TMI
     */
    setupEventHandlers() {
        // Connexion réussie
        this.client.on('connected', (address, port) => {
            // Connecté au serveur
        });

        // Déconnexion
        this.client.on('disconnected', (reason) => {
            // Déconnecté du serveur
            this.isConnected = false;
            this.handleReconnect();
        });

        // Rejoint un canal
        this.client.on('join', (channel, username, self) => {
            if (self) {
                // Rejoint le canal
                this.connectedChannels.add(channel.replace('#', ''));
            }
        });

        // Quitte un canal
        this.client.on('part', (channel, username, self) => {
            if (self) {
                // Quitté le canal
                this.connectedChannels.delete(channel.replace('#', ''));
            }
        });

        // Nouveau message
        this.client.on('message', (channel, tags, message, self) => {
            if (self) return; // Ignorer nos propres messages

            // Notifier tous les gestionnaires de messages avec les paramètres TMI.js
            this.messageHandlers.forEach(handler => {
                try {
                    handler(channel, tags, message, self);
                } catch (error) {
                    console.error('Erreur dans le gestionnaire de message:', error);
                }
            });
        });

        // Erreurs
        this.client.on('error', (error) => {
            console.error('❌ Erreur TMI:', error);
        });
    }

    /**
     * Gère la reconnexion automatique
     */
    handleReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            
            // Tentative de reconnexion
            
            setTimeout(() => {
                this.init();
            }, delay);
        } else {
            console.error('❌ Nombre maximum de tentatives de reconnexion atteint');
        }
    }

    /**
     * Rejoint un canal Twitch
     */
    async joinChannel(channelName) {
        if (!this.isConnected || !this.client) {
            console.warn('Client non connecté, impossible de rejoindre le canal:', channelName);
            return false;
        }

        try {
            await this.client.join(channelName);
            // Rejoint le canal
            return true;
        } catch (error) {
            console.error(`❌ Erreur lors de la connexion au canal ${channelName}:`, error);
            return false;
        }
    }

    /**
     * Quitte un canal Twitch
     */
    async leaveChannel(channelName) {
        if (!this.isConnected || !this.client) {
            return false;
        }

        try {
            await this.client.part(channelName);
            // Quitté le canal
            return true;
        } catch (error) {
            console.error(`❌ Erreur lors de la déconnexion du canal ${channelName}:`, error);
            return false;
        }
    }

    /**
     * Ajoute un gestionnaire de messages
     */
    addMessageHandler(handler) {
        this.messageHandlers.add(handler);
    }

    /**
     * Supprime un gestionnaire de messages
     */
    removeMessageHandler(handler) {
        this.messageHandlers.delete(handler);
    }

    /**
     * Génère une couleur aléatoire pour les utilisateurs sans couleur
     */
    generateRandomColor() {
        const colors = [
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    }

    /**
     * Obtient la liste des canaux connectés
     */
    getConnectedChannels() {
        return Array.from(this.connectedChannels);
    }

    /**
     * Vérifie si le client est connecté
     */
    isClientConnected() {
        return this.isConnected && this.client;
    }

    /**
     * Déconnecte le client
     */
    async disconnect() {
        if (this.client && this.isConnected) {
            try {
                await this.client.disconnect();
                // Client Twitch Chat déconnecté
            } catch (error) {
                console.error('Erreur lors de la déconnexion:', error);
            }
        }
        this.isConnected = false;
        this.connectedChannels.clear();
        this.messageHandlers.clear();
    }
}
